import React, { useState, useMemo } from "react";
import { useList, useApiUrl } from "@refinedev/core";
import axios from "axios";
import Markdown from "react-markdown";
import { useDownloadTracker } from "@/hooks/useDownloadTracker";
import { VaultCommentPanel } from "@/components/digibox/VaultCommentPanel";

type SortField = "title" | "filetype" | "filesize" | "createdtime";
type SortDir = "asc" | "desc";

export const ArchiveList: React.FC = () => {
  const apiUrl = useApiUrl();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("createdtime");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{ url: string; mime: string; docId: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);
  const [editingFiscalDate, setEditingFiscalDate] = useState(false);
  const [fiscalDateValue, setFiscalDateValue] = useState("");
  const { markDownloaded, isDownloaded } = useDownloadTracker();

  const { data: docsData, isLoading } = useList({
    resource: "documents",
    pagination: { pageSize: 500 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  const allDocs = (docsData?.data || []) as any[];

  const { years, fileTypes } = useMemo(() => {
    const yrs = new Set<string>();
    const types = new Set<string>();
    for (const d of allDocs) {
      if (d.createdtime) yrs.add(new Date(d.createdtime).getFullYear().toString());
      const ext = normalizeType(d.filetype, d.filename);
      if (ext) types.add(ext);
    }
    return { years: [...yrs].sort((a, b) => b.localeCompare(a)), fileTypes: [...types].sort() };
  }, [allDocs]);

  const filtered = useMemo(() => {
    let docs = allDocs;
    if (yearFilter !== "all") docs = docs.filter((d: any) => d.createdtime && new Date(d.createdtime).getFullYear().toString() === yearFilter);
    if (monthFilter !== "all") docs = docs.filter((d: any) => d.createdtime && (new Date(d.createdtime).getMonth() + 1).toString() === monthFilter);
    if (typeFilter !== "all") docs = docs.filter((d: any) => normalizeType(d.filetype, d.filename) === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      docs = docs.filter((d: any) =>
        (d.notes_title || "").toLowerCase().includes(q) ||
        (d.filename || "").toLowerCase().includes(q) ||
        (d.notecontent || "").toLowerCase().includes(q)
      );
    }
    return [...docs].sort((a: any, b: any) => {
      let va: any, vb: any;
      switch (sortField) {
        case "title": va = (a.notes_title || a.filename || "").toLowerCase(); vb = (b.notes_title || b.filename || "").toLowerCase(); break;
        case "filetype": va = normalizeType(a.filetype, a.filename); vb = normalizeType(b.filetype, b.filename); break;
        case "filesize": va = Number(a.filesize) || 0; vb = Number(b.filesize) || 0; break;
        default: va = a.createdtime || ""; vb = b.createdtime || "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [allDocs, yearFilter, monthFilter, typeFilter, search, sortField, sortDir]);

  const selectedDoc = selectedId ? allDocs.find((d: any) => d.id === selectedId) : null;

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir(field === "title" ? "asc" : "desc"); }
  };
  const sortIndicator = (field: SortField) => sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const fetchFileData = async (doc: any) => {
    const res = await axios.post(`${apiUrl}/direct/retrieve-attachment`, { doc_ids: [doc.id], return_file: true });
    if (!res.data.success) return null;
    const fd = res.data.result[doc.id] || Object.values(res.data.result)[0] as any;
    return fd;
  };

  const handleDownload = async (doc: any) => {
    setDownloading(doc.id);
    try {
      const fd = await fetchFileData(doc);
      const base64 = fd?.filecontent || fd?.attachment;
      if (base64) {
        const fname = fd.filename || doc.notes_title || "download";
        const link = document.createElement("a");
        link.href = `data:${fd.filetype || "application/octet-stream"};base64,${base64}`;
        link.download = fname;
        link.click();
        markDownloaded(doc.id, fname);
      }
    } catch { /* */ } finally { setDownloading(null); }
  };

  const handlePreview = async (doc: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (previewData?.docId === doc.id) { setPreviewData(null); return; }
    setPreviewLoading(doc.id);
    try {
      const fd = await fetchFileData(doc);
      const base64 = fd?.filecontent || fd?.attachment;
      if (base64) {
        const mime = fd.filetype || doc.filetype || "application/pdf";
        setPreviewData({ url: `data:${mime};base64,${base64}`, mime, docId: doc.id });
      }
    } catch { /* */ } finally { setPreviewLoading(null); }
  };

  const canPreview = (d: any) =>
    (d.filetype || d.filename || "").toLowerCase().includes("pdf") ||
    /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(d.filename || "") ||
    (d.filetype || "").startsWith("image/");

  const MONTHS = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
  ];

  return (
    <main className="vault-panel vault-main-content" style={{ gridColumn: "1 / -1" }}>
      <div className="vault-hub-content">

        {/* ── Detail view ──────────────────────────────── */}
        {selectedDoc ? (
          <>
            <div style={{ marginBottom: "var(--vault-spacing-md)" }}>
              <button
                className="vault-btn-ghost"
                onClick={() => { setSelectedId(null); setPreviewData(null); }}
                style={{ fontSize: "var(--vault-text-small)" }}
              >
                ← Torna alla lista
              </button>
            </div>

            <div className="vault-hub-hero">
              <h1 className="vault-heading-display">
                {(selectedDoc.notes_title || selectedDoc.filename || "").toUpperCase()}
              </h1>
            </div>

            {/* Meta row */}
            <div style={{
              display: "flex", gap: "var(--vault-spacing-lg)", flexWrap: "wrap",
              borderBottom: "1px solid var(--vault-grid-line)", paddingBottom: "var(--vault-spacing-sm)",
            }}>
              <div>
                <span className="vault-label-tiny" style={{ display: "block" }}>ID</span>
                <span className="vault-text-small">{selectedDoc.id}</span>
              </div>
              <div>
                <span className="vault-label-tiny" style={{ display: "block" }}>Tipo</span>
                <span className="vault-text-small">{normalizeType(selectedDoc.filetype, selectedDoc.filename).toUpperCase() || "—"}</span>
              </div>
              <div>
                <span className="vault-label-tiny" style={{ display: "block" }}>Dimensione</span>
                <span className="vault-text-small">{formatSize(selectedDoc.filesize)}</span>
              </div>
              <div>
                <span className="vault-label-tiny" style={{ display: "block" }}>File</span>
                <span className="vault-text-small">{selectedDoc.filename || "—"}</span>
              </div>
              {/* Editable fiscal date */}
              <div>
                <span className="vault-label-tiny" style={{ display: "block" }}>Data di Competenza</span>
                {editingFiscalDate ? (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <input
                      type="date"
                      className="vault-modal-input"
                      value={fiscalDateValue}
                      onChange={(e) => setFiscalDateValue(e.target.value)}
                      style={{ padding: "2px 6px", fontSize: "var(--vault-text-small)", width: 140 }}
                    />
                    <button
                      className="vault-btn-ghost"
                      style={{ fontSize: "var(--vault-text-tiny)", padding: "2px 6px" }}
                      onClick={async () => {
                        if (fiscalDateValue) {
                          await axios.post(`${apiUrl}/direct/update`, {
                            id: selectedDoc.id,
                            data: { fiscal_date: fiscalDateValue },
                          });
                          selectedDoc.fiscal_date = fiscalDateValue;
                        }
                        setEditingFiscalDate(false);
                      }}
                    >
                      ✓
                    </button>
                    <button
                      className="vault-btn-ghost"
                      style={{ fontSize: "var(--vault-text-tiny)", padding: "2px 6px" }}
                      onClick={() => setEditingFiscalDate(false)}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <span
                    className="vault-text-small"
                    style={{ cursor: "pointer", borderBottom: "1px dashed var(--vault-grid-line-strong)" }}
                    onClick={() => {
                      setFiscalDateValue(selectedDoc.fiscal_date || "");
                      setEditingFiscalDate(true);
                    }}
                    title="Clicca per modificare"
                  >
                    {selectedDoc.fiscal_date ? formatDate(selectedDoc.fiscal_date) : "— imposta"}
                  </span>
                )}
              </div>
              <div style={{ marginLeft: "auto" }}>
                <span className="vault-label-tiny" style={{ display: "block" }}>Caricato il</span>
                <span className="vault-text-small">{formatDate(selectedDoc.createdtime)}</span>
              </div>
            </div>

            {/* Content */}
            {selectedDoc.notecontent && (
              <div className="vault-text-body-editorial" style={{ marginTop: "var(--vault-spacing-md)" }}>
                <Markdown>{selectedDoc.notecontent}</Markdown>
              </div>
            )}
            {selectedDoc.filedescription && (
              <p className="vault-text-muted" style={{ marginTop: "var(--vault-spacing-sm)" }}>
                {selectedDoc.filedescription}
              </p>
            )}

            {/* Actions */}
            <div className="vault-action-bar" style={{ marginTop: "var(--vault-spacing-md)" }}>
              <div className="vault-action-statement">
                {selectedDoc.filename || selectedDoc.notes_title}
              </div>
              <div style={{ display: "flex", gap: "var(--vault-spacing-xs)" }}>
                <button
                  className="vault-btn"
                  onClick={() => setCommentsPanelOpen((v) => !v)}
                  style={{
                    minWidth: 130,
                    background: commentsPanelOpen ? "var(--vault-fg-black)" : "transparent",
                    color: commentsPanelOpen ? "#fff" : "var(--vault-fg-black)",
                  }}
                >
                  <span>✉ Commenti</span>
                </button>
                {canPreview(selectedDoc) && (
                  <button
                    className="vault-btn"
                    onClick={(e) => handlePreview(selectedDoc, e)}
                    style={{ minWidth: 130 }}
                  >
                    <span>{previewData?.docId === selectedDoc.id ? "Chiudi" : previewLoading === selectedDoc.id ? "..." : "▷ Anteprima"}</span>
                  </button>
                )}
                <button
                  className="vault-btn"
                  onClick={() => handleDownload(selectedDoc)}
                  disabled={downloading === selectedDoc.id}
                  style={{ minWidth: 130 }}
                >
                  <span>{downloading === selectedDoc.id ? "..." : "↓ Scarica"}</span>
                </button>
              </div>
            </div>

            {/* Inline preview */}
            {previewData && previewData.docId === selectedDoc.id && (
              <div style={{ marginTop: "var(--vault-spacing-sm)", border: "1px solid var(--vault-grid-line)", background: "#f5f5f5" }}>
                {previewData.mime.includes("pdf") ? (
                  <iframe src={previewData.url} style={{ width: "100%", height: 700, border: "none" }} title="Anteprima PDF" />
                ) : (
                  <img src={previewData.url} alt="Anteprima" style={{ width: "100%", maxHeight: 700, objectFit: "contain" }} />
                )}
              </div>
            )}

            {/* Comment panel */}
            <VaultCommentPanel
              relatedTo={selectedDoc.id}
              open={commentsPanelOpen}
              onClose={() => setCommentsPanelOpen(false)}
            />
          </>
        ) : (
          /* ── List view ──────────────────────────────── */
          <>
            <div className="vault-hub-hero">
              <h1 className="vault-heading-display">ARCHIVIO<br />DOCUMENTI</h1>
            </div>

            {/* Filter bar */}
            <div style={{
              display: "flex", alignItems: "center", gap: "var(--vault-spacing-sm)",
              paddingBottom: "var(--vault-spacing-sm)", borderBottom: "1px solid var(--vault-grid-line)", flexWrap: "wrap",
            }}>
              <input className="vault-modal-input" placeholder="Cerca..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 220, padding: "6px 12px", fontSize: "var(--vault-text-small)" }} />
              <select className="vault-modal-input" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}
                style={{ width: 100, padding: "6px 8px", fontSize: "var(--vault-text-tiny)", textTransform: "uppercase" }}>
                <option value="all">Anno</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="vault-modal-input" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
                style={{ width: 120, padding: "6px 8px", fontSize: "var(--vault-text-tiny)", textTransform: "uppercase" }}>
                <option value="all">Mese</option>
                {MONTHS.map((m, i) => <option key={i} value={(i + 1).toString()}>{m}</option>)}
              </select>
              <select className="vault-modal-input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                style={{ width: 100, padding: "6px 8px", fontSize: "var(--vault-text-tiny)", textTransform: "uppercase" }}>
                <option value="all">Tipo</option>
                {fileTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {(yearFilter !== "all" || monthFilter !== "all" || typeFilter !== "all" || search) && (
                <button className="vault-btn-ghost" onClick={() => { setYearFilter("all"); setMonthFilter("all"); setTypeFilter("all"); setSearch(""); }}
                  style={{ fontSize: "var(--vault-text-tiny)" }}>✕ Reset</button>
              )}
              <span className="vault-label-tiny" style={{ marginLeft: "auto" }}>
                {filtered.length} / {allDocs.length} documenti
              </span>
            </div>

            {/* Table */}
            {isLoading ? (
              <div className="vault-loading">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="vault-empty" style={{ padding: "var(--vault-spacing-lg)" }}>Nessun documento trovato</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--vault-grid-line-strong)" }}>
                    <Th onClick={() => handleSort("title")} label={`Nome${sortIndicator("title")}`} />
                    <Th onClick={() => handleSort("filetype")} label={`Tipo${sortIndicator("filetype")}`} width={90} />
                    <Th onClick={() => handleSort("filesize")} label={`Dim.${sortIndicator("filesize")}`} width={80} align="right" />
                    <Th onClick={() => handleSort("createdtime")} label={`Data${sortIndicator("createdtime")}`} width={110} align="right" />
                    <th style={{ width: 180 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d: any) => (
                    <React.Fragment key={d.id}>
                      <tr
                        style={{
                          borderBottom: previewData?.docId === d.id ? "none" : "1px solid var(--vault-grid-line)",
                          cursor: "pointer",
                          background: selectedId === d.id ? "#f5f5f5" : undefined,
                        }}
                        onMouseEnter={(e) => { if (selectedId !== d.id) e.currentTarget.style.background = "#fafafa"; }}
                        onMouseLeave={(e) => { if (selectedId !== d.id) e.currentTarget.style.background = ""; }}
                        onClick={() => setSelectedId(d.id)}
                      >
                        <td style={{ padding: "var(--vault-spacing-xs) 0" }}>
                          <span className="vault-text-small" style={{ fontWeight: 500 }}>
                            {d.notes_title || d.filename || "Documento"}
                          </span>
                          {isDownloaded(d.id) && (
                            <span
                              style={{
                                display: "inline-block",
                                background: "var(--vault-zone-accepted-border)",
                                color: "#fff",
                                fontSize: "0.5rem",
                                padding: "1px 5px",
                                borderRadius: 2,
                                marginLeft: 6,
                                fontWeight: 600,
                                verticalAlign: "middle",
                              }}
                            >
                              ✓ SCARICATO
                            </span>
                          )}
                          {d.notecontent && (
                            <span className="vault-text-muted" style={{
                              display: "block", fontSize: "var(--vault-text-tiny)",
                              maxWidth: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {d.notecontent.replace(/<[^>]*>/g, "").slice(0, 120)}
                            </span>
                          )}
                        </td>
                        <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)", textTransform: "uppercase" }}>
                          {normalizeType(d.filetype, d.filename)}
                        </td>
                        <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)", textAlign: "right" }}>
                          {formatSize(d.filesize)}
                        </td>
                        <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)", textAlign: "right" }}>
                          {formatDate(d.createdtime)}
                        </td>
                        <td style={{ padding: "var(--vault-spacing-xs) 0", textAlign: "right" }}>
                          <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                            {canPreview(d) && (
                              <button className="vault-btn-ghost" onClick={(e) => handlePreview(d, e)}
                                disabled={previewLoading === d.id}
                                style={{ fontSize: "var(--vault-text-tiny)" }}>
                                {previewLoading === d.id ? "..." : previewData?.docId === d.id ? "Chiudi" : "▷"}
                              </button>
                            )}
                            <button className="vault-btn-ghost" onClick={(e) => { e.stopPropagation(); handleDownload(d); }}
                              disabled={downloading === d.id}
                              style={{ fontSize: "var(--vault-text-tiny)" }}>
                              {downloading === d.id ? "..." : "↓ Scarica"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Inline preview row */}
                      {previewData && previewData.docId === d.id && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0, borderBottom: "1px solid var(--vault-grid-line)" }}>
                            <div style={{ background: "#f5f5f5", border: "1px solid var(--vault-grid-line)", borderTop: "none" }}>
                              {previewData.mime.includes("pdf") ? (
                                <iframe src={previewData.url} style={{ width: "100%", height: 500, border: "none" }} title="Anteprima" />
                              ) : (
                                <img src={previewData.url} alt="Anteprima" style={{ width: "100%", maxHeight: 500, objectFit: "contain" }} />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </main>
  );
};

/* ── Helpers ──────────────────────────────────────────────── */

function Th({ onClick, label, width, align = "left" }: {
  onClick: () => void; label: string; width?: number; align?: "left" | "right";
}) {
  return (
    <th className="vault-label-tiny" onClick={onClick}
      style={{ textAlign: align, padding: "var(--vault-spacing-xs) 0", fontWeight: 500, width, cursor: "pointer", userSelect: "none" }}>
      {label}
    </th>
  );
}

function normalizeType(filetype?: string, filename?: string): string {
  if (filename) { const ext = filename.split(".").pop()?.toLowerCase(); if (ext && ext.length <= 5) return ext; }
  if (!filetype) return "";
  return filetype.replace("application/", "").replace("image/", "").replace("text/", "").split(";")[0].toLowerCase();
}

function formatSize(size?: string | number): string {
  const n = Number(size);
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d?: string): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
